/*  upload_allfunds.tsx
    – Upload the AllFunds Transactions export into trade_dates/{settlementDate}/trades
      under the GLOBAL_DPM pseudo-account. Every row (in-flight included) becomes a
      trade doc keyed GLOBAL_DPM_{transactionCode}; re-uploads merge-update the same
      doc as the AllFunds status progresses.
      Two dates matter: the SETTLEMENT date is the bucket and tradeDate (operations
      view / Geneva / Burgan all work off it), the ORDER date is stamped as
      orderDateId (the DPM allocation excel and the DPM team views key off it).
      The AllFunds Value Date is only a fallback while settlement is unknown.
*/
import React, { useState } from 'react';
import {
    Badge,
    Button,
    Card,
    FileInput,
    Group,
    Loader,
    NumberFormatter,
    Stack,
    Table,
    Text,
    Title,
} from '@mantine/core';
import * as XLSX from 'xlsx';
import {
    DocumentReference,
    Timestamp,
    collection,
    collectionGroup,
    doc,
    getDocs,
    getFirestore,
    query,
    where,
    writeBatch,
} from 'firebase/firestore';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { ALLFUNDS_DEAD_STATUSES, Trade, TradeAllocation, qtyEq, stripNoise } from '../../types';

const GLOBAL_DPM_ACCOUNT = 'GLOBAL_DPM';

/* The fee AllFunds charges lives in 'Asset Mgmt. Co. Fee' — it becomes the trade's
   totalCommission (same field equities use), and Gross − Net Amount Awarded reflects it.
   The other fee/commission columns are expected to stay zero; a non-zero value there
   is surfaced as a warning since it wouldn't be carried onto the trade. */
const FEE_COLUMN = 'Asset Mgmt. Co. Fee';
const UNEXPECTED_FEE_COLUMNS = [
    'Commission per Fund',
    'Initial Charge',
    'Discount',
    'Commission',
    'Dilution Levy',
    'Exit Charge',
    'Commissions Applied (Distributor)',
    'Expenses Applied by AFB',
    'Commission Amount',
    'Fee Amount 1',
    'Fee Amount 2',
];

const KNOWN_STATUSES = [
    'Acknowledged by Asset Mgmt. Co',
    'Cancelled',
    'Ccy exchange pending',
    'Confirmed',
    'Confirmed. Anticipated payment emitted',
    'Error',
    'Estimated confirmation. Anticipated payment emitted',
    'Finalised',
    'Partial',
    'Pending',
    'Pending to be authorised by client',
    'Registered',
    'Sent to Asset Mgmt. Co',
    'Sent to Asset Mgmt. Co - Pending Cancellation',
    'Settled',
    'Submitted ccy exchange pending',
    'Validated by user',
];

/* the file writes "Sent to Asset Mgmt. Co." with a trailing period; the portal vocabulary has none */
const normalizeStatus = (s: string): string => s.trim().replace(/\.$/, '');

/* "-" is the file's null; numbers are plain numerics */
const num = (v: unknown): number => {
    if (v === '-' || v === '' || v === null || v === undefined) return 0;
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

/* Dates are MM/DD/YYYY strings ("-" when missing); handle Date objects defensively
   in case cellDates conversion kicks in on a differently-typed export. */
const parseMDY = (v: unknown): Date | null => {
    if (v instanceof Date && !isNaN(v.getTime())) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
    if (typeof v !== 'string') return null;
    const s = v.trim();
    if (!s || s === '-') return null;
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    return new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
};

const toDateId = (d: Date): string =>
    `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;

interface ParsedRow {
    code: string;
    orderType: string;
    status: string; // normalized
    side: 'BUY' | 'SELL';
    isin: string;
    name: string;
    orderedShares: number; // absolute
    sharesAwarded: number; // absolute
    nav: number;
    fee: number; // 'Asset Mgmt. Co. Fee' (usually 0)
    grossAmountRequested: number;
    grossAmountAwarded: number; // full amount incl. fees
    netAmountAwarded: number; // amount without fees
    currency: string;
    orderDate: Date; // DPM allocation excel match key
    valueDate: Date | null; // settlement fallback only
    settlementDate: Date | null;
    dateId: string; // toDateId(settlementDate ?? valueDate ?? orderDate) — the trade_dates bucket
    allocatable: boolean;
    dead: boolean; // Cancelled / Error
}

interface ExistingTrade {
    ref: DocumentReference;
    data: Trade;
    dateId: string;
}

type RowAction = 'create' | 'update' | 'relocate' | 'delete' | 'flag' | 'skip';

interface PlannedRow {
    row: ParsedRow;
    action: RowAction;
    existing?: ExistingTrade;
    note?: string;
}

const ACTION_COLORS: Record<RowAction, string> = {
    create: 'green',
    update: 'blue',
    relocate: 'orange',
    delete: 'red',
    flag: 'red',
    skip: 'gray',
};

const statusColor = (row: ParsedRow): string =>
    row.dead ? 'red' : row.allocatable ? 'green' : 'yellow';

const sumAllocations = (allocations?: TradeAllocation[]): number =>
    (allocations || []).reduce((s, a) => s + (a.qty || 0), 0);

const UploadAllFunds = () => {
    const [file, setFile] = useState<File | null>(null);
    const [processing, setProcessing] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [plan, setPlan] = useState<PlannedRow[]>([]);

    const db = getFirestore();

    const reset = () => {
        setErrors([]);
        setWarnings([]);
        setPlan([]);
    };

    /* ---------- parse one sheet row ---------- */
    const parseRow = (raw: Record<string, unknown>, err: string[], warn: string[]): ParsedRow | null => {
        const code = String(raw['Transaction Code'] ?? '').trim();
        if (!code || code === '-') return null; // blank filler row

        const orderType = String(raw['Order Type'] ?? '').trim();
        const status = normalizeStatus(String(raw['Status'] ?? ''));
        if (!KNOWN_STATUSES.includes(status)) {
            warn.push(`${code}: unknown status "${status}" — treated as in-flight unless NAV settles it`);
        }

        const rawOrdered = num(raw['Ordered Shares']);
        const rawAwarded = num(raw['Shares Awarded']);
        /* Known Order Type vocabulary: "SUBS. BY SHARES.", "REDEMPTION BY SHARES.", … —
           an unrecognized type (e.g. a future SWITCH/TRANSFER) must never silently
           default to BUY, so it's a hard error the team escalates. */
        const isRedemption = orderType.toUpperCase().includes('REDEMPTION');
        const isSubscription = orderType.toUpperCase().includes('SUBS');
        if (!isRedemption && !isSubscription) {
            err.push(`${code}: unrecognized Order Type "${orderType}" — cannot determine buy/sell side`);
            return null;
        }
        const side: 'BUY' | 'SELL' = isRedemption ? 'SELL' : 'BUY';
        if ((rawAwarded < 0 || rawOrdered < 0) !== isRedemption) {
            warn.push(`${code}: order type "${orderType}" disagrees with share sign (${rawAwarded || rawOrdered})`);
        }

        /* the ORDER date is the DPM allocation excel's match key — required */
        const orderDate = parseMDY(raw['Order Date']);
        if (!orderDate) {
            err.push(`${code}: missing/invalid Order Date "${raw['Order Date']}"`);
            return null;
        }
        const valueDate = parseMDY(raw['Value Date']);
        const settlementDate = parseMDY(raw['Settlement Date']);
        /* the trade lives on its SETTLEMENT date (operations / Geneva / Burgan);
           while in-flight the settlement is unknown, so it starts on the value
           date (or order date) and relocates on a later re-upload */
        const bucketDate = settlementDate ?? valueDate ?? orderDate;

        /* Funds are identified by ISIN everywhere downstream (security enrichment,
           excel-row matching) — a blank ISIN would create a permanently unusable trade. */
        const isin = String(raw['ISIN Code'] ?? '').trim();
        if (!isin) {
            err.push(`${code}: missing ISIN Code`);
            return null;
        }

        const fee = Math.abs(num(raw[FEE_COLUMN]));
        for (const col of UNEXPECTED_FEE_COLUMNS) {
            if (num(raw[col]) !== 0) {
                warn.push(`${code}: unexpected non-zero fee column "${col}" = ${raw[col]} — not carried onto the trade, verify with AllFunds`);
            }
        }

        const nav = num(raw['NAV']);
        const dead = ALLFUNDS_DEAD_STATUSES.includes(status);
        const sharesAwarded = Math.abs(rawAwarded);

        return {
            code,
            orderType,
            status,
            side,
            isin,
            name: String(raw['Name'] ?? '').trim(),
            orderedShares: Math.abs(rawOrdered),
            sharesAwarded,
            nav,
            fee,
            grossAmountRequested: Math.abs(num(raw['Gross Amount Requested'])),
            grossAmountAwarded: Math.abs(num(raw['Gross Amount Awarded'])),
            netAmountAwarded: Math.abs(num(raw['Net Amount Awarded'])),
            currency: String(raw['Fund Currency'] ?? raw['Currency'] ?? 'USD').trim() || 'USD',
            orderDate,
            valueDate,
            settlementDate,
            dateId: toDateId(bucketDate),
            // only Settled rows are allocatable — final NAV and awarded units
            allocatable: status === 'Settled' && nav > 0 && sharesAwarded > 0,
            dead,
        };
    };

    /* ---------- diff parsed rows against existing GLOBAL_DPM trades ---------- */
    const buildPlan = (rows: ParsedRow[], existingByCode: Map<string, ExistingTrade>, warn: string[]): PlannedRow[] =>
        rows.map((row) => {
            const existing = existingByCode.get(row.code);

            if (!existing) {
                if (row.dead) return { row, action: 'skip', note: `${row.status} row never uploaded before — not created` };
                return { row, action: 'create' };
            }

            const allocatedQty = sumAllocations(existing.data.allocations);
            const isAllocated = allocatedQty > 0;
            const isLocked = existing.data.state === 'LOCKED';

            if (row.dead) {
                if (isLocked || isAllocated) {
                    warn.push(`${row.code}: became ${row.status} but the trade is ${isLocked ? 'LOCKED in Geneva' : 'allocated'} — flagged, resolve manually then re-upload`);
                    return { row, action: 'flag', existing, note: 'cancelled after allocation' };
                }
                return { row, action: 'delete', existing, note: `now ${row.status}` };
            }

            if (existing.dateId !== row.dateId) {
                if (isLocked) {
                    warn.push(`${row.code}: Settlement Date moved ${existing.dateId} → ${row.dateId} but the trade is LOCKED in Geneva — not relocated, resolve manually`);
                    return { row, action: 'flag', existing, note: 'locked trade date moved' };
                }
                return { row, action: 'relocate', existing, note: `${existing.dateId} → ${row.dateId}` };
            }

            if (isAllocated && (!qtyEq(existing.data.filledQty, row.sharesAwarded) || !qtyEq(existing.data.nav ?? existing.data.price, row.nav))) {
                warn.push(`${row.code}: qty/NAV changed on an allocated trade (${existing.data.filledQty} @ ${existing.data.nav ?? existing.data.price} → ${row.sharesAwarded} @ ${row.nav}) — allocations no longer sum, adjust them`);
            }

            return { row, action: 'update', existing };
        });

    /* ---------- build the Trade doc fields for a row ---------- */
    const buildTradeFields = (row: ParsedRow, existing?: ExistingTrade): Partial<Trade> => {
        const allocatedQty = stripNoise(sumAllocations(existing?.data.allocations));
        const settle = row.settlementDate ?? row.valueDate ?? row.orderDate;
        const fields: Partial<Trade> = {
            id: `${GLOBAL_DPM_ACCOUNT}_${row.code}`,
            execId: Number(row.code),
            originalExecId: row.code,
            accountId: GLOBAL_DPM_ACCOUNT,
            side: row.side,
            price: row.nav,
            orderQty: row.orderedShares || row.sharesAwarded,
            filledQty: row.sharesAwarded,
            pendingQty: 0,
            /* Straight from the file, on the same fields equities use:
               orderValue = Net Amount Awarded (amount without fees),
               netSettle = Gross Amount Awarded (full amount incl. fees),
               totalCommission = Asset Mgmt. Co. Fee.
               While in-flight on an amount order there are no awarded numbers yet —
               orderValue shows the requested amount so the team sees what was ordered. */
            orderValue: row.netAmountAwarded || row.grossAmountRequested,
            netSettle: row.grossAmountAwarded || row.netAmountAwarded,
            totalCommission: row.fee,
            symbol: row.name,
            symbolDesc: row.name,
            symbolCode: row.isin, // securities join key — funds are identified by ISIN
            currency: row.currency,
            /* Geneva (EventDate/SettleDate) and the Burgan file (Trx Date/TrxTime) read
               tradeDate — the settlement date, at 12:00 so TrxTime isn't midnight */
            tradeDate: Timestamp.fromDate(new Date(settle.getFullYear(), settle.getMonth(), settle.getDate(), 12)),
            settlementDate: Timestamp.fromDate(settle),
            unallocQty: stripNoise(Math.max(0, row.sharesAwarded - allocatedQty)),
            exchange: 'ALLFUNDS',
            subMarket: 'ALLFUNDS',
            source: 'allfunds',
            isin: row.isin,
            nav: row.nav,
            allfundsTransactionCode: row.code,
            allfundsStatus: row.status,
            /* the DPM allocation excel and the DPM team views key by ORDER date */
            orderDateId: toDateId(row.orderDate),
            /* the DPM allocation excel reconciles its amounts (buffer included)
               against Gross Amount Requested (see global_dpm_matching.ts) */
            grossAmountRequested: row.grossAmountRequested,
        };
        if (!existing) {
            /* filler for the required Trade fields that have no AllFunds equivalent —
               only on create so re-uploads never clobber anything */
            Object.assign(fields, {
                tradeTime: '12:00:00',
                customerNo: '',
                customerName: '',
                exchOrderId: '',
                dealer: '',
                tif: '',
                institution: 'AllFunds',
                exchangeCommission: 0,
                type: row.orderType,
                channel: '',
                customerRefNo: '',
                cashAcrRef: '',
                executingBrokerId: '',
                dealerExtension: '',
                securityAccount: '',
                orderId: '',
                createdAt: Timestamp.now(),
            });
        }
        return fields;
    };

    /* ---------- file handling ---------- */
    const handleFileUpload = async (f: File) => {
        setFile(f);
        setProcessing(true);
        reset();

        try {
            const data = await f.arrayBuffer();
            const workbook = XLSX.read(data, { type: 'array', cellDates: true });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });

            const err: string[] = [];
            const warn: string[] = [];
            const rows: ParsedRow[] = [];
            const seen = new Set<string>();
            for (const raw of json) {
                const row = parseRow(raw, err, warn);
                if (!row) continue;
                if (seen.has(row.code)) {
                    err.push(`${row.code}: duplicate Transaction Code inside the file`);
                    continue;
                }
                seen.add(row.code);
                rows.push(row);
            }
            if (rows.length === 0) {
                err.push('No AllFunds rows found — is this the Transactions export?');
            }

            /* one collection-group query finds every existing GLOBAL_DPM trade across dates */
            const existingByCode = new Map<string, ExistingTrade>();
            if (rows.length > 0) {
                const snapshot = await getDocs(
                    query(collectionGroup(db, 'trades'), where('accountId', '==', GLOBAL_DPM_ACCOUNT)),
                );
                snapshot.docs.forEach((d) => {
                    const t = d.data() as Trade;
                    const code = t.allfundsTransactionCode;
                    if (code) {
                        existingByCode.set(code, { ref: d.ref, data: t, dateId: d.ref.parent.parent!.id });
                    }
                });
            }

            setPlan(buildPlan(rows, existingByCode, warn));
            setErrors(err);
            setWarnings(warn);
        } catch (e: any) {
            console.error(e);
            setErrors([e.message || 'Failed to parse spreadsheet']);
        }
        setProcessing(false);
    };

    /* ---------- Firestore submit ---------- */
    const submitData = async () => {
        const actionable = plan.filter((p) => p.action !== 'skip' && p.action !== 'flag');
        const flagged = plan.filter((p) => p.action === 'flag');
        if (actionable.length === 0 && flagged.length === 0) return;
        setUpdating(true);

        try {
            /* 1. parent date docs FIRST — the trade trigger does a bare update() on the
               parent and dies if it doesn't exist yet */
            const dateIds = new Set<string>();
            actionable.forEach((p) => {
                dateIds.add(p.row.dateId);
                if (p.existing) dateIds.add(p.existing.dateId);
            });
            const parentBatch = writeBatch(db);
            dateIds.forEach((dateId) => {
                const [dd, mm, yyyy] = dateId.split('-').map(Number);
                parentBatch.set(
                    doc(db, 'trade_dates', dateId),
                    { date: Timestamp.fromDate(new Date(yyyy, mm - 1, dd)) },
                    { merge: true },
                );
            });
            await parentBatch.commit();

            /* 2. trade writes */
            const BATCH_LIMIT = 400;
            let batch = writeBatch(db);
            let writes = 0;
            const commits: Promise<void>[] = [];
            const bump = () => {
                if (++writes === BATCH_LIMIT) {
                    commits.push(batch.commit());
                    batch = writeBatch(db);
                    writes = 0;
                }
            };

            for (const p of actionable) {
                const { row, existing } = p;
                const newRef = doc(collection(db, 'trade_dates', row.dateId, 'trades'), `${GLOBAL_DPM_ACCOUNT}_${row.code}`);
                switch (p.action) {
                    case 'delete':
                        batch.delete(existing!.ref);
                        bump();
                        break;
                    case 'relocate': {
                        /* carry allocations over to the new date; stats on the old date
                           are decremented by the delete trigger */
                        const fields = buildTradeFields(row);
                        Object.assign(fields, {
                            createdAt: existing!.data.createdAt ?? Timestamp.now(),
                            ...(existing!.data.allocations ? {
                                allocations: existing!.data.allocations,
                                // buildTradeFields(row) has no `existing`, so recompute
                                // unallocQty for the carried-over allocations
                                unallocQty: stripNoise(Math.max(0, row.sharesAwarded - sumAllocations(existing!.data.allocations))),
                            } : {}),
                        });
                        /* the delete+create pair must commit atomically in ONE batch —
                           if only one slot is left, roll the batch over first */
                        if (writes >= BATCH_LIMIT - 1) {
                            commits.push(batch.commit());
                            batch = writeBatch(db);
                            writes = 0;
                        }
                        batch.delete(existing!.ref);
                        bump();
                        batch.set(newRef, fields, { merge: true });
                        bump();
                        break;
                    }
                    default: // create / update
                        batch.set(newRef, buildTradeFields(row, existing), { merge: true });
                        bump();
                }
            }
            /* flagged rows: only stamp the live status so the table shows the cancellation */
            for (const p of flagged) {
                batch.set(p.existing!.ref, { allfundsStatus: p.row.status }, { merge: true });
                bump();
            }
            if (writes > 0) commits.push(batch.commit());
            await Promise.all(commits);

            notifications.show({
                title: 'AllFunds upload complete',
                message: `${actionable.length} trades written${flagged.length ? `, ${flagged.length} flagged for manual review` : ''}`,
                icon: <IconCheck />,
                color: 'green',
            });
            setFile(null);
            setPlan([]);
        } catch (e) {
            console.error(e);
            notifications.show({
                title: 'AllFunds upload failed',
                message: 'Some batches failed – check console',
                icon: <IconX />,
                color: 'red',
            });
        }
        setUpdating(false);
    };

    /* ---------- summary counts ---------- */
    const counts: Record<RowAction, number> = { create: 0, update: 0, relocate: 0, delete: 0, flag: 0, skip: 0 };
    plan.forEach((p) => counts[p.action]++);
    const allocatableCount = plan.filter((p) => p.row.allocatable && p.action !== 'skip').length;
    const inFlightCount = plan.filter((p) => !p.row.allocatable && !p.row.dead && p.action !== 'skip').length;
    const totalFees = stripNoise(plan.filter((p) => p.action !== 'skip').reduce((s, p) => s + p.row.fee, 0));

    /* ---------- UI ---------- */
    return (
        <Card shadow="sm" p="xl" radius="md" withBorder mt="md">
            <Title order={5} mb="xs">AllFunds Transactions</Title>
            <FileInput
                label="Upload AllFunds Transactions export"
                placeholder="Choose Excel file"
                accept="application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                value={file}
                onChange={(f) => f && handleFileUpload(f)}
            />

            {processing && (
                <Group mt="md">
                    <Loader size="sm" /> <Text>Parsing…</Text>
                </Group>
            )}

            {!processing && plan.length > 0 && (
                <Stack mt="md" gap="md">
                    <Group gap="lg">
                        <Text>
                            <Text fw={700} span>Rows:</Text> <NumberFormatter value={plan.length} thousandSeparator />
                        </Text>
                        <Text c="green"><Text fw={700} span>Allocatable:</Text> {allocatableCount}</Text>
                        <Text c="yellow"><Text fw={700} span>In flight:</Text> {inFlightCount}</Text>
                        {totalFees !== 0 && (
                            <Text><Text fw={700} span>Total fees:</Text> <NumberFormatter value={totalFees} thousandSeparator decimalScale={2} /></Text>
                        )}
                        {(['create', 'update', 'relocate', 'delete', 'flag', 'skip'] as RowAction[])
                            .filter((a) => counts[a] > 0)
                            .map((a) => (
                                <Badge key={a} color={ACTION_COLORS[a]} variant="light">
                                    {a}: {counts[a]}
                                </Badge>
                            ))}
                    </Group>

                    {warnings.length > 0 && (
                        <Stack gap={2}>
                            <Text c="orange" fw={700}>Warnings:</Text>
                            {warnings.map((msg, i) => (
                                <Text key={i} c="orange" size="sm">- {msg}</Text>
                            ))}
                        </Stack>
                    )}
                    {errors.length > 0 && (
                        <Stack gap={2}>
                            <Text c="red" fw={700}>Errors:</Text>
                            {errors.map((msg, i) => (
                                <Text key={i} c="red" size="sm">- {msg}</Text>
                            ))}
                        </Stack>
                    )}

                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Code</Table.Th>
                                <Table.Th>Order Type</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>ISIN</Table.Th>
                                <Table.Th>Fund</Table.Th>
                                <Table.Th>Side</Table.Th>
                                <Table.Th>Units</Table.Th>
                                <Table.Th>NAV</Table.Th>
                                <Table.Th>Amount</Table.Th>
                                <Table.Th>Fee</Table.Th>
                                <Table.Th>Settlement Date</Table.Th>
                                <Table.Th>Action</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {plan.map((p) => (
                                <Table.Tr key={p.row.code}>
                                    <Table.Td>{p.row.code.replace(/^0+/, '') || '0'}</Table.Td>
                                    <Table.Td>{p.row.orderType}</Table.Td>
                                    <Table.Td>
                                        <Badge color={statusColor(p.row)} variant="light">{p.row.status}</Badge>
                                    </Table.Td>
                                    <Table.Td>{p.row.isin}</Table.Td>
                                    <Table.Td>{p.row.name}</Table.Td>
                                    <Table.Td>{p.row.side}</Table.Td>
                                    <Table.Td>
                                        <NumberFormatter value={p.row.sharesAwarded || p.row.orderedShares} thousandSeparator />
                                    </Table.Td>
                                    <Table.Td>{p.row.nav || '—'}</Table.Td>
                                    <Table.Td>
                                        <NumberFormatter value={p.row.netAmountAwarded || p.row.grossAmountRequested} thousandSeparator decimalScale={2} />
                                        {p.row.grossAmountAwarded !== p.row.netAmountAwarded && p.row.grossAmountAwarded > 0 && (
                                            <Text size="xs" c="dimmed">
                                                incl. fees: <NumberFormatter value={p.row.grossAmountAwarded} thousandSeparator decimalScale={2} />
                                            </Text>
                                        )}
                                    </Table.Td>
                                    <Table.Td>
                                        {p.row.fee !== 0
                                            ? <Text size="sm" c="orange" fw={500}><NumberFormatter value={p.row.fee} thousandSeparator decimalScale={2} /></Text>
                                            : <Text size="sm" c="dimmed">—</Text>}
                                    </Table.Td>
                                    <Table.Td>{p.row.dateId}</Table.Td>
                                    <Table.Td>
                                        <Badge color={ACTION_COLORS[p.action]} variant="light">{p.action}</Badge>
                                        {p.note && <Text size="xs" c="dimmed">{p.note}</Text>}
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>

                    <Button
                        mt="md"
                        onClick={submitData}
                        loading={updating}
                        disabled={errors.length > 0 || plan.every((p) => p.action === 'skip')}
                    >
                        Submit to Firestore
                    </Button>
                </Stack>
            )}

            {!processing && plan.length === 0 && errors.length > 0 && (
                <Stack mt="sm" gap={2}>
                    {errors.map((msg, i) => (
                        <Text key={i} c="red" size="sm">- {msg}</Text>
                    ))}
                </Stack>
            )}
        </Card>
    );
};

export default UploadAllFunds;
